/**
 * <zknl-keuzehulp> v2 — 5-vragen-flow (Bob, 24 jul) die eindigt op het juiste
 * product mét de juiste variant voorgeselecteerd.
 *
 * Zonder JS: elke antwoordknop is een gewone link naar een zinnige
 * collectie/pagina. Met JS: de flow verzamelt {hoogte, voetsteun, wielen,
 * rug, lijn}, kiest het doelproduct (lijn × rugleuning, handles uit
 * data-kh-products), haalt /products/<handle>.js op en zoekt fuzzy de
 * matchende variant — optienamen en -waarden verschillen per lijn
 * ("Voetsteun" vs "Voetensteun", "Glijdoppen" vs "Voetdoppen", …).
 */
class ZknlKeuzehulp extends HTMLElement {
  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-zknl-keuzehulp-step]'));
    this.resultStep = this.querySelector('[data-zknl-keuzehulp-result]');
    if (this.steps.length < 2) return;

    try {
      this.products = JSON.parse(this.getAttribute('data-kh-products') || '{}');
    } catch (e) {
      this.products = {};
    }
    this.keuzes = {};

    this.nav = this.querySelector('.zknl-keuzehulp__nav');
    this.backBtn = this.querySelector('[data-zknl-keuzehulp-back]');
    this.progressEl = this.querySelector('[data-zknl-keuzehulp-progress]');
    this.current = 0;

    this.setAttribute('data-js', '');
    if (this.nav) this.nav.hidden = false;

    this.steps.forEach((step, index) => {
      step.querySelectorAll('[data-zknl-keuzehulp-answer]').forEach((answer) => {
        answer.addEventListener('click', (event) => {
          event.preventDefault();
          answer.parentElement
            .querySelectorAll('[data-zknl-keuzehulp-answer]')
            .forEach((a) => a.classList.remove('is-selected'));
          answer.classList.add('is-selected');

          const field = answer.getAttribute('data-kh-field');
          if (field) this.keuzes[field] = answer.getAttribute('data-kh-value');

          if (index < this.steps.length - 1) {
            this.goTo(index + 1);
          } else {
            this.finish(answer.getAttribute('href'));
          }
        });
      });
    });

    if (this.backBtn) {
      this.backBtn.addEventListener('click', () => this.goTo(this.current - 1));
    }

    this.goTo(0);
  }

  goTo(index) {
    if (index < 0 || index >= this.steps.length) return;
    this.current = index;
    this.steps.forEach((step, i) => step.classList.toggle('is-active', i === index));
    if (this.resultStep) {
      this.resultStep.hidden = true;
      this.resultStep.classList.remove('is-active');
    }
    if (this.backBtn) this.backBtn.hidden = index === 0;
    if (this.progressEl) {
      const labels = (window.ZknlKeuzehulpStrings && window.ZknlKeuzehulpStrings.stepLabels) || [];
      this.progressEl.textContent = labels[index] || `${index + 1} / ${this.steps.length}`;
    }
  }

  finish(fallbackUrl) {
    // Toon "we zoeken…" en resolve naar product + variant.
    this.steps.forEach((step) => step.classList.remove('is-active'));
    if (this.resultStep) {
      this.resultStep.hidden = false;
      this.resultStep.classList.add('is-active');
      this.resultStep.style.display = 'block';
    }
    if (this.nav) this.nav.hidden = true;

    const key = (this.keuzes.lijn || 'basic') + (this.keuzes.rug === 'ja' ? '_rug' : '');
    const handle = this.products[key];
    if (!handle) {
      window.location.href = fallbackUrl || '/collections/zadelkrukken';
      return;
    }
    const productUrl = '/products/' + handle;

    fetch(productUrl + '.js')
      .then((r) => r.json())
      .then((product) => {
        const variant = this.matchVariant(product);
        window.location.href = variant ? productUrl + '?variant=' + variant.id : productUrl;
      })
      .catch(() => {
        window.location.href = productUrl;
      });
  }

  matchVariant(product) {
    // Rollen per optie-index bepalen (namen verschillen per lijn).
    const optionNames = (product.options || []).map((o) =>
      (typeof o === 'string' ? o : o.name || '').toLowerCase()
    );
    const idx = {
      hoogte: optionNames.findIndex((n) => n.includes('hoogte')),
      voetsteun: optionNames.findIndex((n) => n.includes('voet') && !n.includes('wiel')),
      wielen: optionNames.findIndex((n) => n.includes('wiel')),
    };

    const normHoogte = (v) => {
      const s = (v || '').toLowerCase();
      if (s.startsWith('laag') || s.startsWith('lage')) return 'laag';
      if (s.includes('standaard')) return 'standaard';
      if (s.startsWith('hoog') || s.startsWith('hoge')) return 'hoog';
      return '';
    };
    const normVoet = (v) => ((v || '').toLowerCase().includes('zonder') ? 'nee' : 'ja');
    const normWiel = (v) => {
      const s = (v || '').toLowerCase();
      if (s.includes('voetdop') || s.includes('glijdop')) return 'dop';
      if (s.includes('softgel')) return 'softgel';
      if (s.includes('onbelast')) return 'onbelast';
      if (s.includes('gelagerd')) return 'gelagerd';
      if (s.includes('premium') && s.includes('zacht')) return 'premiumzacht';
      if (s.includes('premium')) return 'premium';
      if (s.includes('zacht')) return 'zacht';
      if (s.includes('standaard')) return 'standaard';
      return '';
    };

    const past = (variant) => {
      const opts = [variant.option1, variant.option2, variant.option3];
      if (idx.hoogte >= 0 && this.keuzes.hoogte && normHoogte(opts[idx.hoogte]) !== this.keuzes.hoogte) return false;
      if (idx.voetsteun >= 0 && this.keuzes.voetsteun && normVoet(opts[idx.voetsteun]) !== this.keuzes.voetsteun) return false;
      if (idx.wielen >= 0 && this.keuzes.wielen && normWiel(opts[idx.wielen]) !== this.keuzes.wielen) return false;
      return true;
    };

    const variants = product.variants || [];
    return variants.find((v) => past(v) && v.available) || variants.find(past) || null;
  }
}

if (!customElements.get('zknl-keuzehulp')) {
  customElements.define('zknl-keuzehulp', ZknlKeuzehulp);
}
